import React, { useState } from "react";
import { CalendlyLink } from "../SiteHeader";
import { maxPrice, money, qFromSnap } from "./math";

const INCOMES = [85000, 110000, 150000];

// House style by share of debt-free buying power kept.
const HOUSES = [
  [0.97, "house-fancy", "The fancy one", "Front-attached garage. Bonus room. Neighbours who wave."],
  [0.86, "house-average", "The classic", "Average size, no garage, room for a garden and a dog."],
  [0.74, "house-duplex", "The duplex", "Half a house, whole lot of sense."],
  [0.62, "house-townhouse", "The townhouse", "Shared walls. Shared snow removal. Not shared mortgage."],
  [0, "house-apartment", "The apartment", "Condo fees included. Truck not."],
];
// Vehicle by monthly payment.
const RIDES = [
  [0, "vehicle-bike", "The bike", "Zero payments. Chef's kiss."],
  [1, "vehicle-car", "The hatchback", "Compact. Sensible. Paid off by spring."],
  [501, "vehicle-suv", "The SUV", "Three rows. Heated everything. Hockey bags."],
  [1001, "vehicle-truck", "The half-ton", "Crew cab. Hemi. Obviously."],
  [1501, "vehicle-bigtruck", "The dually", "Lifted. Light bar. Stacks. Tow package. Roughly one bedroom."],
];
const pick = (rows, v, cmp) => rows.find(([t]) => cmp(v, t));

export function DebtSlider({ snap }) {
  const [income, setIncome] = useState(110000);
  const [debts, setDebts] = useState(500);
  const q = qFromSnap(snap);
  const down = Math.round(income * 0.4);
  const base = maxPrice({ income, down, debts: 0, q })?.price || 0;
  const now = maxPrice({ income, down, debts, q })?.price || 0;
  const per500 = (maxPrice({ income, down, debts: 500, q })?.price || 0) - (maxPrice({ income, down, debts: 1000, q })?.price || 0);
  const lost = base - now;
  const ratio = base ? now / base : 1;
  const [, houseImg, houseK, houseV] = pick(HOUSES, ratio, (a, b) => a >= b);
  const [, rideImg, rideK, rideV] = [...RIDES].reverse().find(([t]) => debts >= t);
  return (
    <section className="ds" data-testid="debt-slider">
      <div className="wrap ds-grid">
        <div className="ds-top">
          <div className="eyebrow">Under the hood · live math</div>
          <h2 className="display ds-h2">Every $500 a month in payments costs you up to <em>{money(per500)}</em> of house.</h2>
          <p className="ds-lead">Slide it. Watch the house change. Then put the truck back.</p>
          <div className="ds-income">
            <span>Household income</span>
            {INCOMES.map((v) => <button type="button" key={v} className={`pq-chip${income === v ? " on" : ""}`} onClick={() => setIncome(v)} data-testid={`debt-income-${v}`}>${(v / 1000)}k</button>)}
          </div>
          <label className="ds-slider">
            <span className="ds-slider-head"><span>Monthly debt payments</span><strong data-testid="debt-value">{money(debts)}<small>/mo</small></strong></span>
            <input type="range" min="0" max="2000" step="50" value={debts} onChange={(e) => setDebts(Number(e.target.value))} data-testid="debt-range" />
            <span className="ds-ticks"><i>$0</i><i>Car payment</i><i>Truck payment</i><i>$2,000</i></span>
          </label>
        </div>
        <div className="ds-art" aria-live="polite">
          <figure className="ds-tier ds-house" data-testid="debt-house" data-tier={houseImg}>
            <img key={houseImg} src={`/brand/slider/${houseImg}.png`} alt={houseK} />
            <figcaption><b>{houseK}</b><span>{houseV}</span></figcaption>
          </figure>
          <figure className="ds-tier ds-ride" data-testid="debt-ride" data-tier={rideImg}>
            <img key={rideImg} src={`/brand/slider/${rideImg}.png`} alt={rideK} />
            <figcaption><b>{rideK}</b><span>{rideV}</span></figcaption>
          </figure>
        </div>
        <div className="ds-bottom">
          <div className="ds-out">
            <div><span className="tag">House you can buy</span><strong data-testid="debt-price">{money(now)}</strong></div>
            <div className={lost > 0 ? "hit" : ""}><span className="tag">That payment costs you</span><strong data-testid="debt-lost">{lost > 0 ? `−${money(lost)}` : "$0"}</strong><em>{lost > 0 ? "of house" : "Chef's kiss."}</em></div>
          </div>
          <div className="ds-cta"><CalendlyLink className="btn outline" testId="debt-cta">Want the truck and the house? Let's talk</CalendlyLink><span className="micro">Assumes {money(down)} down · stress-tested at {q.qualifyingRate}% · {q.amort}-year amortization.</span></div>
        </div>
      </div>
    </section>
  );
}
